<template>
  <div>
    <FiltersWrapper>
      <CollapseBlock class="filters" tablet-mode>
        <div class="filters__item">
          <CustomSelect
            :options="this.residents"
            label="ЖК"
            optionValueKey="resident_id"
            optionLabelKey="resident_name"
            :value="this.filters.resident_id"
            :loading="this.residentsFetching"
            @change="(value) => onFiltersChange('resident_id', value)"
          />
        </div>
        <div class="filters__item">
          <CustomSelect
            :options="this.blocks"
            label="Блок"
            optionValueKey="block_id"
            optionLabelKey="block_name"
            :value="this.filters.block_id"
            :loading="this.blocksFetching"
            :disabled="!this.filters.resident_id"
            @change="(value) => onFiltersChange('block_id', value)"
          />
        </div>
        <div class="filters__item">
          <CustomSelect
            :options="this.workGroups"
            label="Конструктив"
            optionValueKey="work_group_id"
            optionLabelKey="work_group_name"
            :value="this.filters.work_group_id"
            :loading="this.workGroupsFetching"
            :disabled="!this.filters.block_id"
            @change="(value) => onFiltersChange('work_group_id', value)"
          />
        </div>
      </CollapseBlock>
    </FiltersWrapper>
    <div class="table-container">
      <div v-if="!this.filters.work_group_id" class="no-data">
        Выберите конструктив для отображения данных
      </div>
      <div v-else-if="dateRangeError" class="error-message">
        {{ dateRangeError }}
      </div>
      <div v-else>
        <div class="table-header-with-nav">
          <div class="table-title-section">
            <span class="table-title">План работ</span>
            <div v-if="selectedWorkGroup?.contractor_fio" class="contractor-info">
              Ответственный прораб: {{ selectedWorkGroup.contractor_fio }}
            </div>
          </div>
          <div class="copy-controls">
            <q-btn
              dense
              round
              flat
              :color="isCopyMode ? 'primary' : 'grey-5'"
              :icon="isCopyMode ? 'grid_on' : 'grid_off'"
              :title="isCopyMode ? 'Режим копирования: включен' : 'Режим копирования: выключен'"
              @click="isCopyMode = !isCopyMode"
            />
            <q-btn
              dense
              round
              flat
              :color="isFillMode ? 'primary' : 'grey-5'"
              icon="open_with"
              :title="isFillMode ? 'Режим растягивания: включен' : 'Режим растягивания: выключен'"
              @click="isFillMode = !isFillMode"
            />
            <q-btn
              dense
              round
              flat
              :color="(!isCopyMode || !selectionStart || !selectionEnd) ? 'grey-5' : 'primary'"
              icon="content_copy"
              :disable="!isCopyMode || !selectionStart || !selectionEnd"
              title="Копировать выделенный диапазон (Ctrl+C)"
              @click="onCopyButtonClick"
            />
            <q-btn
              dense
              round
              flat
              :color="(!isCopyMode || !copiedSelection || !activeCell) ? 'grey-5' : 'primary'"
              icon="content_paste"
              :disable="!isCopyMode || !copiedSelection || !activeCell"
              title="Вставить скопированный диапазон (Ctrl+V)"
              @click="onPasteButtonClick"
            />
            <q-btn
              dense
              round
              flat
              :color="!lastAction ? 'grey-5' : 'negative'"
              icon="undo"
              :disable="!lastAction"
              title="Отменить последнее действие"
              @click="onUndoClick"
            />
            <q-btn
              dense
              round
              flat
              :color="(!canDeleteForward() || isDeletingForward) ? 'grey-5' : 'negative'"
              icon="delete_forever"
              :disable="!canDeleteForward() || isDeletingForward"
              :loading="isDeletingForward"
              title="Удалить все значения справа по этой строке"
              @click="onDeleteForwardClick"
            />
            <q-btn
              dense
              round
              flat
              color="grey-7"
              icon="help_outline"
              title="Справка по режимам редактирования"
              @click="openToolbarGuide"
            />
          </div>
        </div>
        <div class="table-wrapper" ref="tableWrapper">
          <CustomTable
            :rowsData="this.rowsData"
            :columns="this.workTaskPlanColumns"
            :loading="isFetching"
            :small="true"
            :striped="false"
            :customBody="true"
            :customHeader="true"
            :allWidth="false"
          >
            <template #customHeader="props">
              <q-tr>
                <q-th class="task-name-header">Задача</q-th>
                <q-th class="unit-name-header" align="center">Ед. изм.</q-th>
                <q-th
                  v-for="dateInfo in this.visibleDates"
                  :key="dateInfo.original"
                  align="center"
                  :class="[
                    'date-header', 
                    { 
                      'date-header--today': isToday(dateInfo.isoDate),
                      'date-header--weekend': isWeekend(dateInfo.isoDate)
                    }
                  ]"
                >
                  <div class="date-header-content">
                    <div class="date-weekday">{{ getWeekday(dateInfo.isoDate) }}</div>
                    <div class="date-display" :class="{ 'date-changed': dateInfo.isoDate !== dateInfo.originalIsoDate }">{{ formatDateShort(dateInfo.isoDate) }}</div>
                    <div v-if="dateInfo.originalIsoDate && dateInfo.isoDate !== dateInfo.originalIsoDate" class="date-original">{{ formatDateShort(dateInfo.originalIsoDate) }}</div>
                  </div>
                </q-th>
              </q-tr>
            </template>
            <template #customBody="props">
              <!-- Специальная обработка для строки "Выход людей" -->
              <q-tr v-if="props.row.is_labor" :class="getRowClass(props.row)">
                <q-td class="task-name-cell labor-task-cell">
                  <div class="task-info">
                    <div class="task-name labor-task-name">{{ props.row.task_name }}</div>
                  </div>
                </q-td>
                <q-td class="unit-name-cell labor-unit-cell" align="center">
                  {{ props.row.unit_name || '-' }}
                </q-td>
                <q-td
                  v-for="(dateInfo, colIndex) in this.visibleDates"
                  :key="dateInfo.original"
                  align="center"
                  :class="[
                    'date-cell', 
                    'labor-cell',
                    'clickable-cell', 
                    {
                      'date-cell--today': isToday(dateInfo.isoDate),
                      'date-cell--weekend': isWeekend(dateInfo.isoDate),
                      'date-cell--has-value': props.row[dateInfo.original] && props.row[dateInfo.original].length > 0 && props.row[dateInfo.original][0].plan_people,
                      'selected-cell': isCellSelected(props.rowIndex, colIndex),
                      'drag-fill-preview': isDragFillCell(props.rowIndex, colIndex)
                    }
                  ]"
                  @mousedown="(event) => onCellMouseDown(event, props.row, dateInfo, props.row[dateInfo.original], props.rowIndex, colIndex)"
                  @mouseenter="() => onCellMouseEnter(props.rowIndex, colIndex)"
                  @click="(event) => handleCellClick(event, props.row, dateInfo, props.row[dateInfo.original], props.rowIndex, colIndex)"
                >
                  <div class="labor-cell-content">
                    {{ props.row[dateInfo.original] && props.row[dateInfo.original].length > 0 && props.row[dateInfo.original][0].plan_people !== null && props.row[dateInfo.original][0].plan_people !== undefined ? props.row[dateInfo.original][0].plan_people : '-' }}
                  </div>
                </q-td>
              </q-tr>
              <!-- Специальная обработка для строки этажности -->
              <q-tr v-else-if="props.row.task_name === 'Этажность'" :class="getRowClass(props.row)">
                <q-td class="task-name-cell floors-task-cell">
                  <div class="task-info">
                    <div class="task-name floors-task-name">{{ props.row.task_name }}</div>
                  </div>
                </q-td>
                <q-td class="unit-name-cell floors-unit-cell" align="center">
                  {{ props.row.unit_name || '-' }}
                </q-td>
                <q-td
                  v-for="(dateInfo, colIndex) in this.visibleDates"
                  :key="dateInfo.original"
                  align="center"
                  :class="[
                    'date-cell', 
                    'floor-cell',
                    { 
                      'date-cell--today': isToday(dateInfo.isoDate),
                      'date-cell--weekend': isWeekend(dateInfo.isoDate)
                    }
                  ]"
                  :style="getFloorCellStyle(props.row[dateInfo.original] && props.row[dateInfo.original].length > 0 ? props.row[dateInfo.original][0].plan_value : null)"
                >
                  <div 
                    class="floor-cell-content"
                    :style="{ color: getFloorCellStyle(props.row[dateInfo.original] && props.row[dateInfo.original].length > 0 ? props.row[dateInfo.original][0].plan_value : null).color }"
                  >
                    {{ props.row[dateInfo.original] && props.row[dateInfo.original].length > 0 && props.row[dateInfo.original][0].plan_value !== null && props.row[dateInfo.original][0].plan_value !== undefined ? props.row[dateInfo.original][0].plan_value : '' }}
                  </div>
                </q-td>
              </q-tr>
              <!-- Обычные строки с задачами -->
              <q-tr v-else :class="getRowClass(props.row)">
                <q-td class="task-name-cell">
                  {{ props.row.task_name }}
                </q-td>
                <q-td class="unit-name-cell" align="center">
                  {{ props.row.unit_name || '-' }}
                </q-td>
                <q-td
                  v-for="(dateInfo, colIndex) in this.visibleDates"
                  :key="dateInfo.original"
                  align="center"
                  :class="[
                    'date-cell', 
                    'clickable-cell', 
                    { 
                      'date-cell--today': isToday(dateInfo.isoDate),
                      'date-cell--has-value': props.row[dateInfo.original] && props.row[dateInfo.original].length > 0 && props.row[dateInfo.original][0].plan_value,
                      'date-cell--weekend': isWeekend(dateInfo.isoDate),
                      'selected-cell': isCellSelected(props.rowIndex, colIndex),
                      'drag-fill-preview': isDragFillCell(props.rowIndex, colIndex)
                    }
                  ]"
                  @mousedown="(event) => onCellMouseDown(event, props.row, dateInfo, props.row[dateInfo.original], props.rowIndex, colIndex)"
                  @mouseenter="() => onCellMouseEnter(props.rowIndex, colIndex)"
                  @click="(event) => handleCellClick(event, props.row, dateInfo, props.row[dateInfo.original], props.rowIndex, colIndex)"
                >
                  <div class="cell-inner">
                    <div class="cell-content">
                  <template v-if="props.row[dateInfo.original] && props.row[dateInfo.original].length > 0">
                    <template v-for="(item, index) in props.row[dateInfo.original]" :key="index">
                      <span v-if="item.plan_value !== null && item.plan_value !== undefined && item.plan_value !== ''" class="plan-value-text">
                        {{ item.plan_value }}
                      </span>
                      <span v-else class="text-grey-5">-</span>
                    </template>
                  </template>
                  <span v-else class="text-grey-5">-</span>
                    </div>
                    <div
                      v-if="isCopyMode && isFillMode && activeCell && activeCell.rowIndex === props.rowIndex && activeCell.colIndex === colIndex"
                      class="fill-handle"
                      title="Растянуть значение по строке/столбцу"
                    ></div>
                  </div>
                </q-td>
              </q-tr>
            </template>
          </CustomTable>
        </div>
      </div>
    </div>

    <!-- Глобальный гайд по режимам редактирования -->
    <q-dialog v-model="showToolbarGuide" maximized persistent>
      <q-card class="toolbar-guide-card">
        <q-card-section class="toolbar-guide-header">
          <div class="toolbar-guide-title">
            <q-icon name="help_outline" size="24px" class="toolbar-guide-title-icon" />
            <div>
              <div class="title-text">Справка по редактированию плана работ</div>
              <div class="subtitle-text">Копирование, вставка, растягивание, удаление и отмена действий</div>
            </div>
          </div>
          <q-btn dense flat round icon="close" @click="showToolbarGuide = false" />
        </q-card-section>

        <q-separator />

        <q-card-section class="toolbar-guide-body">
          <div class="guide-section">
            <div class="guide-section-title">
              <q-icon name="apps" color="primary" />
              <span>Режим копирования</span>
            </div>
            <div class="guide-section-content">
              <p>
                <b>Иконка сетки</b> в панели над таблицей включает и выключает режим копирования.
              </p>
              <ul>
                <li><b>Включён</b> (синяя иконка) – можно выделять диапазон ячеек мышью и через Shift.</li>
                <li><b>Выключен</b> (серая иконка) – таблица работает в обычном режиме, клики открывают модальные окна.</li>
              </ul>
            </div>
          </div>

          <div class="guide-section">
            <div class="guide-section-title">
              <q-icon name="content_copy" color="primary" />
              <span>Копирование диапазона</span>
            </div>
            <div class="guide-section-content">
              <ol>
                <li>Включите режим копирования.</li>
                <li>Кликните по начальной ячейке, затем с зажатым <b>Shift</b> кликните по конечной ячейке – выделится прямоугольник.</li>
                <li>Нажмите кнопку с иконкой <b>копии</b> или используйте <b>Ctrl+C</b>.</li>
              </ol>
              <p>В буфер попадут значения всех выделенных ячеек (кроме «Выход людей» и «Этажность»).</p>
            </div>
          </div>

          <div class="guide-section">
            <div class="guide-section-title">
              <q-icon name="content_paste" color="primary" />
              <span>Вставка диапазона</span>
            </div>
            <div class="guide-section-content">
              <ol>
                <li>Выберите ячейку, с которой нужно вставлять (она станет активной).</li>
                <li>Нажмите кнопку с иконкой <b>вставки</b> или <b>Ctrl+V</b>.</li>
              </ol>
              <p>Скопированный блок значений вставится, начиная с активной ячейки, <b>как в Excel</b> (со смещением по строкам и столбцам).</p>
            </div>
          </div>

          <div class="guide-section">
            <div class="guide-section-title">
              <q-icon name="open_with" color="primary" />
              <span>Режим растягивания (drag fill)</span>
            </div>
            <div class="guide-section-content">
              <p>
                Для растягивания значения нужно включить <b>режим копирования</b> и <b>режим растягивания</b> (иконка со стрелками).
              </p>
              <ul>
                <li>У активной ячейки появляется маленький оранжевый квадратик в правом нижнем углу.</li>
                <li>Зажмите мышь на ячейке и тяните по строке – значение скопируется по горизонтали.</li>
                <li>Тяните вверх/вниз по одному столбцу – значение скопируется по вертикали.</li>
              </ul>
            </div>
          </div>

          <div class="guide-section">
            <div class="guide-section-title">
              <q-icon name="undo" color="negative" />
              <span>Отмена последнего действия</span>
            </div>
            <div class="guide-section-content">
              <p>Кнопка <b>Undo</b> откатывает:</p>
              <ul>
                <li>Копирование/вставку диапазона;</li>
                <li>Растягивание значения по строке/столбцу;</li>
                <li>Модальное заполнение на одну дату или диапазон дат;</li>
                <li>Удаление значений справа по строке.</li>
              </ul>
              <p>Хранится только <b>одно</b> последнее действие – новое действие затирает предыдущее.</p>
            </div>
          </div>

          <div class="guide-section">
            <div class="guide-section-title">
              <q-icon name="delete_forever" color="negative" />
              <span>Удаление значений справа по строке</span>
            </div>
            <div class="guide-section-content">
              <ol>
                <li>Включите режим копирования.</li>
                <li>Кликните по ячейке нужной задачи (например, 01.11 по строке «Разработка грунта»).</li>
                <li>Нажмите иконку <b>корзины</b> и подтвердите удаление.</li>
              </ol>
              <p>Будут удалены все значения этой задачи <b>начиная с выбранной даты и правее</b>. Операцию можно откатить через Undo.</p>
            </div>
          </div>

          <div class="guide-section">
            <div class="guide-section-title">
              <q-icon name="lightbulb" color="orange" />
              <span>Пример полного сценария</span>
            </div>
            <div class="guide-section-content">
              <ol>
                <li>Включите режим копирования.</li>
                <li>Выделите диапазон 01.11–05.11 по строке задачи (клик по 01.11, затем Shift+клик по 05.11).</li>
                <li>Нажмите «Копировать» или Ctrl+C.</li>
                <li>Кликните по ячейке 10.11 и нажмите «Вставить» – значения перенесутся в 10.11–14.11.</li>
                <li>Если нужно растянуть одно значение – включите режим растягивания и потяните за оранжевый квадратик.</li>
                <li>Если ошиблись – нажмите Undo, чтобы вернуть всё назад.</li>
              </ol>
            </div>
          </div>
        </q-card-section>

        <q-separator />

        <q-card-actions align="right">
          <q-btn flat label="Понятно" color="primary" @click="showToolbarGuide = false" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import CustomSelect from "@/components/form/CustomSelect";
import CollapseBlock from "@/components/common/CollapseBlock";
import FiltersWrapper from "@/components/common/FiltersWrapper";
import CustomTable from "@/components/common/CustomTable";
import { getResidents, getBlocks, getWorkGroups, getWorkTaskPlan, getWorkTaskPlanColumns, updateWorkTaskPlan, deleteWorkTaskPlan, getWorkGroupLevels, normalizeDateString, dateToIso, addDaysIso, getLaborPlan, updateLaborPlan, deleteLaborPlan, copyWorkTaskPlanRange, undoWorkTaskPlan } from "./services";
import { MODAL_NAMES } from "@/constants";
import { useAppStore } from "@/services/store/app";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";

export default {
  name: "TierPlan",
  components: { CustomSelect, CollapseBlock, FiltersWrapper, CustomTable },
  data() {
    return {
      workTaskPlanColumns: [],
      rowsData: [],
      allDates: [], // Все доступные даты
      visibleDates: [], // Видимые даты для отображения
      visibleStartIndex: 0, // Индекс первой видимой даты
      visibleEndIndex: 0, // Индекс последней видимой даты
      levels: [], // Массив этажей (из work_group_level_tab)
      isFetching: false,
      floorDistribution: {}, // Распределение этажей по датам { dateIndex: level_num }
      residents: [],
      blocks: [],
      workGroups: [],
      residentsFetching: false,
      blocksFetching: false,
      workGroupsFetching: false,
      filters: {
        resident_id: '',
        block_id: '',
        work_group_id: '',
      },
      // Для копирования/вставки как в Excel
      selectionStart: null, // { rowIndex, colIndex }
      selectionEnd: null,   // { rowIndex, colIndex }
      activeCell: null,     // { rowIndex, colIndex } - точка вставки
      copiedSelection: null, // { cells: [{rowOffset, colOffset, plan_value}], height, width }
      isCopyMode: false,    // режим копирования
      dragFillActive: false, // активен ли режим растягивания
      dragStart: null,       // { rowIndex, colIndex } начало растягивания
      lastAction: null,      // данные для отмены последнего действия
      isDeletingForward: false, // крутилка при удалении по строке
      isFillMode: false,    // режим растягивания (drag fill)
      showToolbarGuide: false, // полноэкранный гайд
      dateRangeError: null, // ошибка диапазона дат (если date_start/date_end отсутствуют)
    }
  },
  watch: {
    isCopyMode(value) {
      if (!value) {
        // Выключили режим копирования — убираем выделение
        this.selectionStart = null;
        this.selectionEnd = null;
        this.clearBrowserSelection();
      }
    },
    'filters.work_group_id'() {
      // При смене конструктива сбрасываем возможный undo
      this.lastAction = null;
    }
  },
  computed: {
    // Текущая дата в формате YYYY-MM-DD
    todayDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    // Можно ли добавить день слева
    canGoBackward() {
      return this.visibleStartIndex > 0;
    },
    // Можно ли добавить день справа
    canGoForward() {
      return this.allDates && this.visibleEndIndex < this.allDates.length - 1;
    },
    // Выбранный конструктив
    selectedWorkGroup() {
      if (!this.filters.work_group_id) return null;
      return this.workGroups.find(wg => wg.work_group_id === this.filters.work_group_id) || null;
    },
  },
  async mounted() {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('mouseup', this.onGlobalMouseUp);
    await this.loadResidents();
    // Загружаем фильтры из URL после загрузки резидентов
    await this.loadFiltersFromURL();
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('mouseup', this.onGlobalMouseUp);
  },
  methods: {
    async loadResidents() {
      this.residentsFetching = true;
      const res = await getResidents();
      this.residentsFetching = false;
      this.residents = res || [];
    },
    async loadBlocks(resident_id) {
      if (!resident_id) {
        this.blocks = [];
        return;
      }
      this.blocksFetching = true;
      const res = await getBlocks(resident_id);
      this.blocksFetching = false;
      this.blocks = res || [];
    },
    async loadWorkGroups(block_id, skipAutoLoad = false) {
      if (!block_id) {
        this.workGroups = [];
        this.levels = [];
        return;
      }
      this.workGroupsFetching = true;
      const res = await getWorkGroups(block_id);
      this.workGroupsFetching = false;
      this.workGroups = res || [];
      // Автоматически выбираем первую вкладку и загружаем данные
      // только если не загружаем из URL
      if (!skipAutoLoad && this.workGroups.length > 0) {
        const firstWorkGroupId = this.workGroups[0].work_group_id;
        this.filters.work_group_id = firstWorkGroupId;
        this.loadWorkTaskPlan(firstWorkGroupId);
        // Сохраняем автоматически выбранный конструктив в фильтрах (URL + localStorage)
        this.updateURLWithFilters();
      } else if (!skipAutoLoad) {
        this.filters.work_group_id = '';
        this.rowsData = [];
        // Обновляем фильтры, если для блока нет доступных конструктивов
        this.updateURLWithFilters();
      }
    },
    async loadWorkTaskPlan(work_group_id) {
      if (!work_group_id) {
        this.rowsData = [];
        this.workTaskPlanColumns = [];
        this.allDates = [];
        this.visibleDates = [];
        this.levels = [];
        this.floorDistribution = {};
        return;
      }
      
      // Всегда загружаем данные в одну таблицу (без аккордеона)
      await this.loadWorkTaskPlanData(work_group_id);
    },
    async loadLevels(work_group_id) {
      try {
        // Загружаем уровни (этажи)
        const levelsData = await getWorkGroupLevels(work_group_id);
        this.levels = levelsData || [];
        
        // Сортируем этажи по floor_order
        this.levels.sort((a, b) => {
          const orderA = a.floor_order !== null && a.floor_order !== undefined ? a.floor_order : Infinity;
          const orderB = b.floor_order !== null && b.floor_order !== undefined ? b.floor_order : Infinity;
          return orderA - orderB;
        });
      } catch (e) {
        console.error('Error loading levels:', e);
        this.levels = [];
      }
    },
    async loadWorkTaskPlanData(work_group_id) {
      if (!work_group_id) {
        this.rowsData = [];
        this.workTaskPlanColumns = [];
        this.allDates = [];
        this.visibleDates = [];
        this.levels = [];
        this.floorDistribution = {};
        return;
      }
      
      this.isFetching = true;
      try {
        // Проверяем, нужны ли этажи
        const selectedWorkGroup = this.workGroups.find(wg => wg.work_group_id === work_group_id);
        const needLevels = selectedWorkGroup?.is_need_level || selectedWorkGroup?.is_level_need || false;
        
        // Загружаем уровни для генерации дат
        await this.loadLevels(work_group_id);
        
        if (!needLevels) {
          this.floorDistribution = {};
        }
        
        // Загружаем данные плана
        const res = await getWorkTaskPlan(work_group_id, null);
        const groupedData = res?.data || [];
        
        // Загружаем данные о выходе людей
        const laborRes = await getLaborPlan(work_group_id);
        const laborData = laborRes?.data || [];
        
        // Генерируем широкий диапазон дат вокруг сегодняшнего дня
        // (относительно независимый от наличия данных в БД)
        this.allDates = this.generateDateRange();
        
        // Создаем словарь plan данных по task_dict_id, work_group_level_id и plan_date
        const planMap = {};
        groupedData.forEach(group => {
          if (group.items && Array.isArray(group.items) && group.items.length > 0) {
            group.items.forEach(item => {
              const levelId = item.work_group_level_id;
              const planDate = this.normalizeDateString(item.work_date || item.plan_date);
              if (!levelId || !planDate) return;

              const key = `${group.task_dict_id}_${levelId}_${planDate}`;
              planMap[key] = {
                work_task_plan_id: item.work_task_plan_id,
                plan_value: item.plan_value,
                plan_comment: item.plan_comment,
                work_group_level_id: levelId,
                plan_date: planDate,
              };
            });
          }
        });
        
        // Создаем матрицу: задачи по вертикали, даты по горизонтали
        const uniqueTasks = [];
        const taskDictMap = {};
        const taskUnitMap = {};
        
        groupedData.forEach(group => {
          if (group.task_name && !uniqueTasks.includes(group.task_name)) {
            uniqueTasks.push(group.task_name);
            taskDictMap[group.task_name] = group.task_dict_id;
            taskUnitMap[group.task_name] = group.unit_name;
          }
        });
        
        // Создаем строку "Выход людей"
        const laborRow = {
          task_dict_id: null,
          task_name: 'Выход людей',
          unit_name: 'Люди',
          is_labor: true // Флаг для определения строки людей
        };
        
        // Создаем словарь данных о людях по датам
        const laborMap = {};
        laborData.forEach(item => {
          const isoDate = normalizeDateString(item.labor_date);
          if (isoDate) {
            laborMap[isoDate] = {
              labor_plan_fact_id: item.labor_plan_fact_id,
              labor_date: isoDate,
              plan_people: item.plan_people,
              fact_people: item.fact_people
            };
          }
        });
        
        // Для каждой колонки добавляем данные о людях (по isoDate)
        this.allDates.forEach(dateInfo => {
          laborRow[dateInfo.original] = laborMap[dateInfo.isoDate] ? [laborMap[dateInfo.isoDate]] : [];
        });
        
        // Создаем остальные строки задач
        const taskRows = uniqueTasks.map(taskName => {
          const row = {
            task_dict_id: taskDictMap[taskName],
            task_name: taskName,
            unit_name: taskUnitMap[taskName]
          };
          
          // Для каждой колонки добавляем данные плана (ключ = level_id + date)
          this.allDates.forEach(dateInfo => {
            const key = `${taskDictMap[taskName]}_${dateInfo.work_group_level_id}_${dateInfo.plan_date}`;
            row[dateInfo.original] = planMap[key] ? [planMap[key]] : [];
          });
          
          return row;
        });
        
        // Строка "Выход людей" идет первой
        this.rowsData = [laborRow, ...taskRows];
        
        // Устанавливаем начальный диапазон (половина месяца)
        this.setInitialVisibleRange();
        
        // Обновляем видимые даты
        this.updateVisibleDates();
        
        // Генерируем колонки
        this.workTaskPlanColumns = this.generateColumns();
        
        // Распределяем этажи по датам и добавляем строку этажности
        if (this.levels.length > 0) {
          this.distributeFloorsByAllDates();
          this.addFloorsRowToData();
        }
      } catch (e) {
        console.error('Error loading work task plan data:', e);
        this.allDates = [];
        this.visibleDates = [];
      } finally {
        this.isFetching = false;
      }
    },
    normalizeDateString(dateStr) {
      if (!dateStr) return null;
      const trimmed = String(dateStr).trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return trimmed;
      }
      if (trimmed.includes('T')) {
        const date = new Date(trimmed);
        if (isNaN(date.getTime())) return null;
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      if (trimmed.includes('.')) {
        const parts = trimmed.split('.');
        if (parts.length === 3) {
          const [day, month, year] = parts.map(part => parseInt(part, 10));
          if (!Number.isNaN(day) && !Number.isNaN(month) && !Number.isNaN(year)) {
            return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          }
        }
      }
      return null;
    },
    parseDateToTimestamp(dateStr) {
      const normalized = this.normalizeDateString(dateStr);
      if (!normalized) return null;
      const date = new Date(`${normalized}T00:00:00`);
      const timestamp = date.getTime();
      return Number.isNaN(timestamp) ? null : timestamp;
    },
    shouldRenderLevelCell(date, levelCells, monthDates) {
      const cell = this.getLevelCellContainingDate(date, levelCells);
      if (!cell) return false;
      const monthDateSet = new Set(monthDates.map((d) => d.original));
      const firstDateInMonth = cell.dates.find((d) => monthDateSet.has(d));
      return firstDateInMonth === date;
    },
    getLevelCellColspan(date, levelCells, monthDates) {
      const cell = this.getLevelCellContainingDate(date, levelCells);
      if (!cell) return 1;
      const monthDateSet = new Set(monthDates.map((d) => d.original));
      const datesInMonth = cell.dates.filter((d) => monthDateSet.has(d));
      return datesInMonth.length || 1;
    },
    getLevelForDate(date, levelCells) {
      const cell = this.getLevelCellContainingDate(date, levelCells);
      return cell ? cell.level : '-';
    },
    getLevelCellContainingDate(date, levelCells) {
      if (!levelCells || levelCells.length === 0) return null;
      return levelCells.find((cell) => cell.dates && cell.dates.includes(date)) || null;
    },
    onLevelCellMouseEnter(date, levelCells) {
      const cell = this.getLevelCellContainingDate(date, levelCells);
      this.hoveredLevelDates = cell ? [...cell.dates] : [];
    },
    clearLevelHover() {
      this.hoveredLevelDates = [];
    },
    isDateHovered(date) {
      return this.hoveredLevelDatesSet.has(date);
    },
    // Проверка, является ли дата сегодняшней
    isToday(date) {
      return date === this.todayDate;
    },
    getWeekday(isoDate) {
      if (!isoDate) return '';
      const date = new Date(isoDate + 'T00:00:00');
      const weekdays = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
      return weekdays[date.getDay()];
    },
    isWeekend(isoDate) {
      if (!isoDate) return false;
      const date = new Date(isoDate + 'T00:00:00');
      const day = date.getDay();
      return day === 0 || day === 6; // 0 = Воскресенье, 6 = Суббота
    },
    // Проверка, является ли дата первой в месяце
    isFirstDateInMonth(date, monthGroup) {
      return monthGroup && monthGroup.dates && monthGroup.dates.length > 0 && monthGroup.dates[0].original === date;
    },
    // Проверка, является ли дата последней в месяце
    isLastDateInMonth(date, monthGroup) {
      return monthGroup && monthGroup.dates && monthGroup.dates.length > 0 && monthGroup.dates[monthGroup.dates.length - 1].original === date;
    },
    onFiltersChange(key, value) {
      const editedObject = {
        [key]: value,
      };

      if (key === 'resident_id') {
        editedObject.block_id = '';
        editedObject.work_group_id = '';
        if (!!value) {
          this.loadBlocks(value);
        } else {
          this.blocks = [];
          this.workGroups = [];
        }
      }

      if (key === 'block_id') {
        editedObject.work_group_id = '';
        if (!!value) {
          this.loadWorkGroups(value);
        } else {
          this.workGroups = [];
        }
      }

      if (key === 'work_group_id') {
        this.loadWorkTaskPlan(value);
      }

      this.filters = {
        ...this.filters,
        ...editedObject,
      };
      
      // Обновляем URL с текущими фильтрами
      this.updateURLWithFilters();
    },
    // Обновление URL с текущими фильтрами
    updateURLWithFilters() {
      const query = {};
      if (this.filters.resident_id) {
        query.resident_id = this.filters.resident_id;
      }
      if (this.filters.block_id) {
        query.block_id = this.filters.block_id;
      }
      if (this.filters.work_group_id) {
        query.work_group_id = this.filters.work_group_id;
      }
      
      // Сохраняем последний выбранный фильтр в localStorage
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          const storedFilters = {
            resident_id: this.filters.resident_id || null,
            block_id: this.filters.block_id || null,
            work_group_id: this.filters.work_group_id || null,
          };
          window.localStorage.setItem(
            'tierPlanLastFilters',
            JSON.stringify(storedFilters)
          );
        }
      } catch (e) {
        console.error('Error saving tierPlan filters to localStorage:', e);
      }

      // Обновляем URL без перезагрузки страницы
      this.$router.replace({ query }).catch(() => {});
    },
    // Загрузка фильтров из URL при монтировании
    async loadFiltersFromURL() {
      const { resident_id, block_id, work_group_id } = this.$route.query;
      
      if (resident_id) {
        this.filters.resident_id = parseInt(resident_id);
        await this.loadBlocks(this.filters.resident_id);
        
        if (block_id) {
          this.filters.block_id = parseInt(block_id);
          // Передаем skipAutoLoad=true чтобы не загружать автоматически первую группу
          await this.loadWorkGroups(this.filters.block_id, true);
          
          if (work_group_id) {
            this.filters.work_group_id = parseInt(work_group_id);
            await this.loadWorkTaskPlan(this.filters.work_group_id);
          }
        }
        return;
      }

      // Если в URL нет фильтров — пробуем взять последний выбранный из localStorage
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          const stored = window.localStorage.getItem('tierPlanLastFilters');
          if (stored) {
            const parsed = JSON.parse(stored) || {};
            if (parsed.resident_id) {
              this.filters.resident_id = parseInt(parsed.resident_id);
              await this.loadBlocks(this.filters.resident_id);
            }
            if (parsed.block_id) {
              this.filters.block_id = parseInt(parsed.block_id);
              // здесь тоже не автозагружаем первую группу
              await this.loadWorkGroups(this.filters.block_id, true);
            }
            if (parsed.work_group_id) {
              this.filters.work_group_id = parseInt(parsed.work_group_id);
              await this.loadWorkTaskPlan(this.filters.work_group_id);
            }
          }
        }
      } catch (e) {
        console.error('Error loading tierPlan filters from localStorage:', e);
      }
    },
    handleCellClick(event, row, dateInfo, items, rowIndex, colIndex) {
      // Если rowIndex не пришёл из таблицы — вычисляем по rowsData
      if (rowIndex === undefined || rowIndex === null) {
        rowIndex = this.rowsData.findIndex(r => r === row);
      }

      // Режим копирования: клики только для выбора диапазона/точки вставки, без модалок
      if (this.isCopyMode) {
        if (event && event.shiftKey && this.selectionStart) {
          this.selectionEnd = { rowIndex, colIndex };
        } else {
          this.selectionStart = { rowIndex, colIndex };
          this.selectionEnd = { rowIndex, colIndex };
        }
        const rect = this.getSelectionRect();
        if (rect) {
          this.activeCell = { rowIndex: rect.top, colIndex: rect.left };
        }
        this.clearBrowserSelection();
        return;
      }

      // Запоминаем активную ячейку (точка вставки)
      this.activeCell = { rowIndex, colIndex };

      // Не кликаем по строке этажности
      if (row.task_name === 'Этажность') {
        return;
      }
      
      // Получаем первый элемент или создаем пустой объект для новой записи
      const item = items && items.length > 0 ? items[0] : null;
      
      // Получаем информацию о блоке и конструктиве
      const selectedBlock = this.blocks.find(b => b.block_id === this.filters.block_id);
      const selectedWorkGroup = this.workGroups.find(wg => wg.work_group_id === this.filters.work_group_id);

      const isoDate = dateInfo?.isoDate;
      const levelId = dateInfo?.work_group_level_id;
      const planDate = dateInfo?.plan_date;
      if (!isoDate) return;
      
      // Если это строка "Выход людей"
      if (row.is_labor) {
        this.showModal(MODAL_NAMES.EditTierPlan, {
          title: 'Выход людей',
          block_name: selectedBlock?.block_name || '',
          work_group_name: selectedWorkGroup?.work_group_name || '',
          work_group_id: this.filters.work_group_id,
          task_name: 'Выход людей',
          unit_name: 'Люди',
          labor_date: isoDate,
          plan_people: item?.plan_people || '',
          fact_people: item?.fact_people || '',
          labor_plan_fact_id: item?.labor_plan_fact_id || null,
          is_labor: true,
          onSubmit: (formData) => this.onSubmitLaborPlan(formData),
          onDelete: (deleteData) => this.onDeleteLaborPlan(deleteData)
        });
        return;
      }
      
      // Получаем task_dict_id из строки (он теперь хранится в row.task_dict_id)
      const task_dict_id = row.task_dict_id || null;
      
      this.showModal(MODAL_NAMES.EditTierPlan, {
        title: 'План',
        block_name: selectedBlock?.block_name || '',
        work_group_name: selectedWorkGroup?.work_group_name || '',
        work_group_id: this.filters.work_group_id,
        task_name: row.task_name,
        task_dict_id: task_dict_id,
        plan_date: planDate,
        work_group_level_id: levelId,
        plan_value: item?.plan_value || '',
        work_task_plan_id: item?.work_task_plan_id || null,
        onSubmit: (formData) => this.onSubmitPlan(formData),
        onDelete: (deleteData) => this.onDeletePlan(deleteData)
      });
    },
    handleSelectionClick(rowIndex, colIndex) {
      if (rowIndex == null || colIndex == null) return;

      // Если еще нет начала выделения или не зажат Shift – начинаем новое выделение
      if (!this.selectionStart) {
        this.selectionStart = { rowIndex, colIndex };
        this.selectionEnd = { rowIndex, colIndex };
      } else {
        // Расширяем выделение до второй точки
        this.selectionEnd = { rowIndex, colIndex };
      }

      // Точка вставки по умолчанию - верхний левый угол выделения
      const rect = this.getSelectionRect();
      if (rect) {
        this.activeCell = { rowIndex: rect.top, colIndex: rect.left };
      }
    },
    getSelectionRect() {
      if (!this.selectionStart || !this.selectionEnd) return null;
      const top = Math.min(this.selectionStart.rowIndex, this.selectionEnd.rowIndex);
      const bottom = Math.max(this.selectionStart.rowIndex, this.selectionEnd.rowIndex);
      const left = Math.min(this.selectionStart.colIndex, this.selectionEnd.colIndex);
      const right = Math.max(this.selectionStart.colIndex, this.selectionEnd.colIndex);
      return { top, bottom, left, right };
    },
    isCellSelected(rowIndex, colIndex) {
      const rect = this.getSelectionRect();
      if (!rect) return false;
      if (rowIndex == null || colIndex == null) return false;
      return (
        rowIndex >= rect.top &&
        rowIndex <= rect.bottom &&
        colIndex >= rect.left &&
        colIndex <= rect.right
      );
    },
    onKeyDown(event) {
      if (!this.filters.work_group_id) return;
      if (!this.isCopyMode) return;

      // Ctrl+C / Cmd+C – копирование
      if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
        event.preventDefault();
        this.copySelection();
      }

      // Ctrl+V / Cmd+V – вставка
      if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
        event.preventDefault();
        this.pasteSelection();
      }
    },
    copySelection() {
      const rect = this.getSelectionRect();
      if (!rect) return;

      const cells = [];
      const height = rect.bottom - rect.top + 1;
      const width = rect.right - rect.left + 1;

      for (let r = rect.top; r <= rect.bottom; r++) {
        const row = this.rowsData[r];
        if (!row || row.is_labor || row.task_name === 'Этажность') {
          continue;
        }
        if (!row.task_dict_id) {
          continue;
        }

        for (let c = rect.left; c <= rect.right; c++) {
          const dateInfo = this.visibleDates[c];
          if (!dateInfo) continue;
          const dateKey = dateInfo.original;
          const items = row[dateKey] || [];
          const value =
            items && items.length > 0 && items[0].plan_value !== undefined
              ? items[0].plan_value
              : null;

          cells.push({
            rowOffset: r - rect.top,
            colOffset: c - rect.left,
            plan_value: value,
          });
        }
      }

      if (cells.length === 0) {
        this.copiedSelection = null;
        return;
      }

      this.copiedSelection = {
        cells,
        height,
        width,
      };
      this.clearBrowserSelection();

      if (this.$q && this.$q.notify) {
        this.$q.notify({
          type: 'info',
          message: `Скопировано ячеек: ${cells.length}`,
          position: 'top',
        });
      }
    },
    async pasteSelection() {
      if (!this.copiedSelection) return;
      if (!this.activeCell) return;

      const { cells, height, width } = this.copiedSelection;
      const work_group_id = this.filters.work_group_id;

      if (!work_group_id) return;

      const startRow = this.activeCell.rowIndex;
      const startCol = this.activeCell.colIndex;

      if (startRow == null || startCol == null) return;

      const payloadCells = [];

      cells.forEach((cell) => {
        const destRowIndex = startRow + cell.rowOffset;
        const destColIndex = startCol + cell.colOffset;

        const destRow = this.rowsData[destRowIndex];
        const destDateInfo = this.visibleDates[destColIndex];

        if (!destRow || !destDateInfo) {
          return;
        }

        // Пропускаем строку "Выход людей" и этажность
        if (destRow.is_labor || destRow.task_name === 'Этажность') {
          return;
        }

        const task_dict_id = destRow.task_dict_id;
        if (!task_dict_id) {
          return;
        }

        payloadCells.push({
          task_dict_id,
          plan_value: cell.plan_value,
          work_group_level_id: destDateInfo.work_group_level_id,
          plan_date: destDateInfo.plan_date,
        });
      });

      if (payloadCells.length === 0) {
        return;
      }

      try {
        // Формируем снимок прошлых значений для undo
        const undoCells = payloadCells.map((cell) => {
          const destRow = this.rowsData.find(r => r.task_dict_id === cell.task_dict_id);
          const col = this.visibleDates.find(d => d.work_group_level_id === cell.work_group_level_id && d.plan_date === cell.plan_date);
          const prevItems = destRow && col ? (destRow[col.original] || []) : [];
          const prevValue = prevItems && prevItems.length > 0 && prevItems[0].plan_value !== undefined
            ? prevItems[0].plan_value
            : null;
          return {
            task_dict_id: cell.task_dict_id,
            plan_date: null,
            plan_value: prevValue,
            existed_before: prevValue !== null && prevValue !== undefined,
            work_group_level_id: cell.work_group_level_id,
            plan_date: cell.plan_date,
          };
        });

        await copyWorkTaskPlanRange({
          work_group_id,
          cells: payloadCells,
        });

        this.lastAction = {
          type: 'copy_paste',
          work_group_id,
          cells: undoCells,
        };

        this.showSucceedSnackbar();
        await this.reloadDataSilently();
      } catch (e) {
        console.error('Error pasting selection:', e);
      }
    },
    onCopyButtonClick() {
      this.copySelection();
    },
    onPasteButtonClick() {
      this.pasteSelection();
    },
    clearBrowserSelection() {
      try {
        if (window.getSelection) {
          const sel = window.getSelection();
          if (sel && sel.removeAllRanges) {
            sel.removeAllRanges();
          }
        }
      } catch (e) {
        // ignore
      }
    },
    async onUndoClick() {
      if (!this.lastAction || !this.filters.work_group_id) return;

      try {
        const work_group_id = this.lastAction.work_group_id;
        const cells = this.lastAction.cells || [];
        if (!work_group_id || !cells.length) {
          return;
        }

        await undoWorkTaskPlan({
          work_group_id,
          cells,
        });

        this.lastAction = null;
        this.showSucceedSnackbar();
        await this.reloadDataSilently();
      } catch (e) {
        console.error('Error undoing last action:', e);
      }
    },
    onCellMouseDown(event, row, date, items, rowIndex, colIndex) {
      // Растягивание работает только когда включен режим копирования и режим растягивания
      if (!this.isCopyMode || !this.isFillMode) {
        return;
      }
      // Не даем браузеру выделять текст при растягивании
      if (event && event.preventDefault) {
        event.preventDefault();
      }
      this.dragFillActive = true;
      this.dragStart = { rowIndex, colIndex };
      // Начальное выделение - одна ячейка
      this.selectionStart = { rowIndex, colIndex };
      this.selectionEnd = { rowIndex, colIndex };
      this.activeCell = { rowIndex, colIndex };
      this.clearBrowserSelection();
    },
    onCellMouseEnter(rowIndex, colIndex) {
      if (!this.isCopyMode || !this.isFillMode || !this.dragFillActive || !this.dragStart) {
        return;
      }
      this.selectionEnd = { rowIndex, colIndex };
    },
    async onGlobalMouseUp() {
      if (!this.isCopyMode || !this.dragFillActive) {
        this.dragFillActive = false;
        this.dragStart = null;
        return;
      }

      this.dragFillActive = false;

      // После отпускания мыши заполняем диапазон значением исходной ячейки
      try {
        const rect = this.getSelectionRect();
        if (!rect || !this.dragStart) {
          return;
        }

        const startRow = this.dragStart.rowIndex;
        const startCol = this.dragStart.colIndex;
        const rowSpan = rect.bottom - rect.top;
        const colSpan = rect.right - rect.left;

        // Если нет фактического растягивания - ничего не делаем
        if (rowSpan === 0 && colSpan === 0) {
          return;
        }

        const sourceRow = this.rowsData[startRow];
        const sourceDateInfo = this.visibleDates[startCol];
        if (!sourceRow || !sourceDateInfo) {
          return;
        }
        // Не работаем для строки этажности
        if (sourceRow.task_name === 'Этажность' || !sourceRow.task_dict_id) {
          return;
        }

        const sourceItems = sourceRow[sourceDateInfo.original] || [];
        const sourceValue =
          sourceItems && sourceItems.length > 0 && sourceItems[0].plan_value !== undefined
            ? sourceItems[0].plan_value
            : null;

        if (sourceValue === null || sourceValue === undefined) {
          return;
        }

        const work_group_id = this.filters.work_group_id;
        if (!work_group_id) {
          return;
        }

        const payloadCells = [];

        // Определяем направление растягивания
        if (Math.abs(rowSpan) >= Math.abs(colSpan)) {
          // Вертикальное растягивание (по строкам, один столбец)
          const colIndex = startCol;
          const from = Math.min(startRow, rect.top);
          const to = Math.max(startRow, rect.bottom);
          for (let r = from; r <= to; r++) {
            const destRow = this.rowsData[r];
            const destDateInfo = this.visibleDates[colIndex];
            if (!destRow || !destDateInfo) continue;
            if (destRow.is_labor || destRow.task_name === 'Этажность') continue;
            if (!destRow.task_dict_id) continue;
            payloadCells.push({
              task_dict_id: destRow.task_dict_id,
              plan_value: sourceValue,
              work_group_level_id: destDateInfo.work_group_level_id,
          plan_date: destDateInfo.plan_date,
            });
          }
        } else {
          // Горизонтальное растягивание (по столбцам, одна строка)
          const rowIndex = startRow;
          const from = Math.min(startCol, rect.left);
          const to = Math.max(startCol, rect.right);
          const destRow = this.rowsData[rowIndex];
          for (let c = from; c <= to; c++) {
            const destDateInfo = this.visibleDates[c];
            if (!destRow || !destDateInfo) continue;
            if (destRow.is_labor || destRow.task_name === 'Этажность') continue;
            if (!destRow.task_dict_id) continue;
            payloadCells.push({
              task_dict_id: destRow.task_dict_id,
              plan_value: sourceValue,
              work_group_level_id: destDateInfo.work_group_level_id,
          plan_date: destDateInfo.plan_date,
            });
          }
        }

        if (payloadCells.length === 0) {
          return;
        }

        // Формируем снимок прошлых значений для undo
        const undoCells = payloadCells.map((cell) => {
          const destRow = this.rowsData.find(r => r.task_dict_id === cell.task_dict_id);
          const col = this.visibleDates.find(d => d.work_group_level_id === cell.work_group_level_id && d.plan_date === cell.plan_date);
          const prevItems = destRow && col ? (destRow[col.original] || []) : [];
          const prevValue = prevItems && prevItems.length > 0 && prevItems[0].plan_value !== undefined
            ? prevItems[0].plan_value
            : null;
          return {
            task_dict_id: cell.task_dict_id,
            plan_date: null,
            plan_value: prevValue,
            existed_before: prevValue !== null && prevValue !== undefined,
            work_group_level_id: cell.work_group_level_id,
            plan_date: cell.plan_date,
          };
        });

        await copyWorkTaskPlanRange({
          work_group_id,
          cells: payloadCells,
        });

        this.lastAction = {
          type: 'drag_fill',
          work_group_id,
          cells: undoCells,
        };

        this.showSucceedSnackbar();
        await this.reloadDataSilently();
      } catch (e) {
        console.error('Error in drag fill:', e);
      }
    },
    isDragFillCell(rowIndex, colIndex) {
      if (!this.isCopyMode || !this.isFillMode || !this.dragFillActive || !this.dragStart) {
        return false;
      }
      const rect = this.getSelectionRect();
      if (!rect) return false;

      const rowSpan = rect.bottom - rect.top;
      const colSpan = rect.right - rect.left;

      // Вертикальное растягивание (по строкам, один столбец)
      if (Math.abs(rowSpan) >= Math.abs(colSpan)) {
        const col = this.dragStart.colIndex;
        const from = Math.min(this.dragStart.rowIndex, rect.top);
        const to = Math.max(this.dragStart.rowIndex, rect.bottom);
        return (
          colIndex === col &&
          rowIndex >= from &&
          rowIndex <= to
        );
      }

      // Горизонтальное растягивание (по столбцам, одна строка)
      const row = this.dragStart.rowIndex;
      const from = Math.min(this.dragStart.colIndex, rect.left);
      const to = Math.max(this.dragStart.colIndex, rect.right);
      return (
        rowIndex === row &&
        colIndex >= from &&
        colIndex <= to
      );
    },
    canDeleteForward() {
      if (!this.activeCell) return false;
      if (!this.isCopyMode) return false;
      const { rowIndex, colIndex } = this.activeCell;
      if (rowIndex == null || colIndex == null) return false;
      const row = this.rowsData[rowIndex];
      const dateInfo = this.visibleDates[colIndex];
      if (!row || !dateInfo) return false;
      if (row.is_labor || row.task_name === 'Этажность') return false;
      if (!row.task_dict_id) return false;
      return true;
    },
    async onDeleteForwardClick() {
      if (!this.canDeleteForward()) return;

      const { rowIndex, colIndex } = this.activeCell;
      const row = this.rowsData[rowIndex];
      const startDateInfo = this.visibleDates[colIndex];
      const work_group_id = this.filters.work_group_id;

      if (!row || !startDateInfo || !work_group_id) return;

      const startDate = startDateInfo.original;
      const task_dict_id = row.task_dict_id;
      if (!task_dict_id) return;

      const confirmMsg = `Удалить все значения справа по строке "${row.task_name}" начиная с ${this.formatDateShort(startDateInfo.isoDate)}?`;
      if (!window.confirm(confirmMsg)) {
        return;
      }

      // Находим индекс стартовой даты в allDates
      let startAllIndex = this.allDates.findIndex(d => d.original === startDate);
      if (startAllIndex < 0) {
        startAllIndex = 0;
      }

      const deleteIds = [];
      const undoCells = [];

      for (let i = startAllIndex; i < this.allDates.length; i++) {
        const dateKey = this.allDates[i].original;
        const levelId = this.allDates[i].work_group_level_id;
        const planDate = this.allDates[i].plan_date;
        const items = row[dateKey] || [];
        if (!items || items.length === 0) continue;
        const item = items[0];
        const value = item.plan_value;
        const work_task_plan_id = item.work_task_plan_id;
        if (work_task_plan_id == null) continue;

        deleteIds.push(work_task_plan_id);

        if (value !== null && value !== undefined) {
          undoCells.push({
            task_dict_id,
            plan_date: planDate,
            plan_value: value,
            existed_before: true,
            work_group_level_id: levelId,
          });
        }
      }

      if (!deleteIds.length) return;

      this.isDeletingForward = true;

      try {
        // Показываем начальное уведомление о количестве удаляемых записей
        if (this.$q && this.$q.notify) {
          this.$q.notify({
            type: 'info',
            message: `Начало удаления ${deleteIds.length} записей по строке...`,
            position: 'top',
          });
        }

        // Последовательно удаляем записи
        for (const id of deleteIds) {
          await deleteWorkTaskPlan({ work_task_plan_id: id });
        }

        this.lastAction = {
          type: 'delete_forward',
          work_group_id,
          cells: undoCells,
        };

        this.showSucceedSnackbar();
        await this.reloadDataSilently();
      } catch (e) {
        console.error('Error deleting forward:', e);
      } finally {
        this.isDeletingForward = false;
      }
    },
    openToolbarGuide() {
      this.showToolbarGuide = true;
    },
    async onSubmitPlan(formData) {
      try {
        // При создании (work_task_plan_id = null) все обязательные поля должны быть переданы
        // plan_value может быть пустой строкой, но не null при создании
        
        // Если указана дата окончания, создаем записи для диапазона дат
        if (formData.end_date && !formData.work_task_plan_id) {
          const startDateStr = this.normalizeDateString(formData.plan_date);
          const endDateStr = this.normalizeDateString(formData.end_date);

          if (!startDateStr || !endDateStr) {
            this.$q.notify({
              type: 'negative',
              message: 'Некорректная дата начала/окончания',
              position: 'top'
            });
            return;
          }

          const startDate = new Date(`${startDateStr}T00:00:00`);
          const endDate = new Date(`${endDateStr}T00:00:00`);

          if (endDate < startDate) {
            this.$q.notify({
              type: 'negative',
              message: 'Дата окончания должна быть больше даты начала',
              position: 'top'
            });
            return;
          }

          // Берём диапазон колонок из allDates (1 колонка = 1 level_detail_id)
          const cols = (this.allDates || []).filter(c => c.isoDate >= startDateStr && c.isoDate <= endDateStr);
          const totalCols = cols.length;
          
          // Показываем начальное уведомление
          this.$q.notify({
            type: 'info',
            message: `Начало создания ${totalCols} записей...`,
            position: 'top'
          });
          
          // Формируем снимок прошлых значений для undo по диапазону колонок
          const undoCells = cols.map((col) => {
            const row = this.rowsData.find(r => r.task_dict_id === formData.task_dict_id);
            const prevItems = row ? (row[col.original] || []) : [];
            const prevValue = prevItems && prevItems.length > 0 && prevItems[0].plan_value !== undefined
              ? prevItems[0].plan_value
              : null;
            return {
              task_dict_id: formData.task_dict_id || null,
              plan_date: col.plan_date,
              plan_value: prevValue,
              existed_before: prevValue !== null && prevValue !== undefined,
              work_group_level_id: col.work_group_level_id,
            };
          });

          // Создаем записи для каждой колонки
          for (let i = 0; i < cols.length; i++) {
            const col = cols[i];
            await updateWorkTaskPlan({
              work_task_plan_id: null,
              work_group_id: formData.work_group_id,
              task_dict_id: formData.task_dict_id || null,
              plan_value: formData.plan_value !== null && formData.plan_value !== undefined ? formData.plan_value : null,
              work_group_level_id: col.work_group_level_id,
              plan_date: col.plan_date,
            });
          }

          this.lastAction = {
            type: 'modal_range',
            work_group_id: formData.work_group_id,
            cells: undoCells,
          };
        } else {
          // Обычное сохранение для одной даты
          const row = this.rowsData.find(r => r.task_dict_id === formData.task_dict_id);
          const prevItems = row ? (row[formData.plan_date] || []) : [];
          const prevValue = prevItems && prevItems.length > 0 && prevItems[0].plan_value !== undefined
            ? prevItems[0].plan_value
            : null;

          const data = {
            work_task_plan_id: formData.work_task_plan_id || null,
            work_group_id: formData.work_group_id,
            task_dict_id: formData.task_dict_id || null,
            plan_value: formData.plan_value !== null && formData.plan_value !== undefined ? formData.plan_value : null,
            work_group_level_id: formData.work_group_level_id,
            plan_date: formData.plan_date
          };
          await updateWorkTaskPlan(data);

          this.lastAction = {
            type: 'modal_single',
            work_group_id: formData.work_group_id,
            cells: [{
              task_dict_id: formData.task_dict_id || null,
              plan_date: formData.plan_date,
              plan_value: prevValue,
              existed_before: prevValue !== null && prevValue !== undefined,
              work_group_level_id: formData.work_group_level_id,
            }],
          };
        }
        
        this.showSucceedSnackbar();
        this.closeModal();
        // Тихо обновляем данные - Vue реактивно обновит только изменённые ячейки
        await this.reloadDataSilently();
      } catch (e) {
        console.error('Error updating plan:', e);
      }
    },
    async onDeletePlan(deleteData) {
      try {
        await deleteWorkTaskPlan(deleteData);
        this.showSucceedSnackbar();
        this.closeModal();
        // Тихо обновляем данные - Vue реактивно обновит только изменённые ячейки
        await this.reloadDataSilently();
      } catch (e) {
        console.error('Error deleting plan:', e);
      }
    },
    async onSubmitLaborPlan(formData) {
      try {
        // Если указана дата окончания, создаем записи для диапазона дат
        if (formData.end_date && !formData.labor_plan_fact_id) {
          // Нормализуем даты к формату YYYY-MM-DD
          const normalizeDate = (dateStr) => {
            if (!dateStr) return null;
            // Если формат DD.MM.YYYY
            if (dateStr.includes('.')) {
              const [day, month, year] = dateStr.split('.');
              return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            }
            // Если уже формат YYYY-MM-DD
            return dateStr;
          };
          
          const startDateStr = normalizeDate(formData.labor_date);
          const endDateStr = normalizeDate(formData.end_date);
          
          const startDate = new Date(startDateStr);
          const endDate = new Date(endDateStr);
          
          // Проверка, что дата окончания больше даты начала
          if (endDate < startDate) {
            this.$q.notify({
              type: 'negative',
              message: 'Дата окончания должна быть больше даты начала',
              position: 'top'
            });
            return;
          }
          
          // Генерируем массив дат
          const dates = [];
          const currentDate = new Date(startDateStr);
          while (currentDate <= endDate) {
            dates.push(new Date(currentDate).toISOString().split('T')[0]);
            currentDate.setDate(currentDate.getDate() + 1);
          }
          
          const totalDates = dates.length;
          
          // Показываем начальное уведомление
          this.$q.notify({
            type: 'info',
            message: `Начало создания ${totalDates} записей...`,
            position: 'top'
          });
          
          // Создаем записи для каждой даты
          for (let i = 0; i < dates.length; i++) {
            const date = dates[i];
            const data = {
              labor_plan_fact_id: null,
              work_group_id: formData.work_group_id,
              labor_date: date,
              plan_people: formData.plan_people !== null && formData.plan_people !== undefined ? formData.plan_people : null,
              fact_people: formData.fact_people !== null && formData.fact_people !== undefined ? formData.fact_people : null
            };
            await updateLaborPlan(data);
          }
        } else {
          // Обычное сохранение для одной даты
          const data = {
            labor_plan_fact_id: formData.labor_plan_fact_id || null,
            work_group_id: formData.work_group_id,
            labor_date: formData.labor_date,
            plan_people: formData.plan_people !== null && formData.plan_people !== undefined ? formData.plan_people : null,
            fact_people: formData.fact_people !== null && formData.fact_people !== undefined ? formData.fact_people : null
          };
          await updateLaborPlan(data);
        }
        
        this.showSucceedSnackbar();
        this.closeModal();
        // Тихо обновляем данные - Vue реактивно обновит только изменённые ячейки
        await this.reloadDataSilently();
      } catch (e) {
        console.error('Error updating labor plan:', e);
      }
    },
    async onDeleteLaborPlan(deleteData) {
      try {
        await deleteLaborPlan(deleteData);
        this.showSucceedSnackbar();
        this.closeModal();
        // Тихо обновляем данные - Vue реактивно обновит только изменённые ячейки
        await this.reloadDataSilently();
      } catch (e) {
        console.error('Error deleting labor plan:', e);
      }
    },
    // Генерируем колонки из периодов этажей (level_date_start - level_date_end)
    generateDateRange() {
      this.dateRangeError = null;

      if (!this.levels || this.levels.length === 0) {
        this.dateRangeError = 'Ошибка: не найдены этажи для выбранного конструктива';
        return [];
      }

      const cols = [];
      
      // Проходим по всем этажам
      this.levels.forEach((level) => {
        const startDate = this.normalizeDateString(level.level_date_start);
        const endDate = this.normalizeDateString(level.level_date_end);
        
        if (!startDate || !endDate) return;
        
        // Генерируем даты от start до end
        let currentDate = startDate;
        while (currentDate <= endDate) {
          const [y, m, day] = currentDate.split('-').map((x) => parseInt(x, 10));
          cols.push({
            // original используется как ключ поля в строках (должен быть уникальным)
            original: `${level.work_group_level_id}_${currentDate}`,
            isoDate: currentDate,
            originalIsoDate: currentDate,
            day: day,
            month: m,
            year: y,
            work_group_level_id: level.work_group_level_id,
            plan_date: currentDate,
            level_num: level.level_num,
            floor_order: level.floor_order,
          });
          currentDate = addDaysIso(currentDate, 1);
        }
      });

      // Сортируем по floor_order, затем по дате
      cols.sort((a, b) => {
        const orderA = a.floor_order !== null && a.floor_order !== undefined ? a.floor_order : 999999;
        const orderB = b.floor_order !== null && b.floor_order !== undefined ? b.floor_order : 999999;
        if (orderA !== orderB) return orderA - orderB;
        return a.isoDate.localeCompare(b.isoDate);
      });

      return cols;
    },
    setInitialVisibleRange() {
      if (!this.allDates || this.allDates.length === 0) {
        this.visibleStartIndex = 0;
        this.visibleEndIndex = 0;
        return;
      }
      
      // Показываем ВСЕ даты (из периодов этажей или конструктива)
      this.visibleStartIndex = 0;
      this.visibleEndIndex = this.allDates.length - 1;
    },
    updateVisibleDates() {
      // Обновляем массив видимых дат
      if (!this.allDates || this.allDates.length === 0) {
        this.visibleDates = [];
        return;
      }
      
      const start = Math.max(0, this.visibleStartIndex);
      const end = Math.min(this.allDates.length, this.visibleEndIndex + 1);
      this.visibleDates = this.allDates.slice(start, end);
    },
    generateColumns() {
      const columns = [
        {
          name: "task_name",
          label: "Задача",
          align: "left",
          field: "task_name",
        },
        {
          name: "unit_name",
          label: "Ед. изм.",
          align: "center",
          field: "unit_name",
        },
      ];
      
      // Каждая видимая дата - одна колонка
      this.visibleDates.forEach(dateInfo => {
        columns.push({
          name: dateInfo.original,
          label: this.formatDateShort(dateInfo.original),
          align: "center",
          field: dateInfo.original,
        });
      });
      
      return columns;
    },
    goBackward() {
      if (this.canGoBackward) {
        this.visibleStartIndex = Math.max(0, this.visibleStartIndex - 1);
        this.updateVisibleDates();
        this.workTaskPlanColumns = this.generateColumns();
        
        // Скроллим таблицу влево
        this.$nextTick(() => {
          const wrapper = this.$refs.tableWrapper;
          if (wrapper) {
            wrapper.scrollLeft = 0;
          }
        });
      }
    },
    goForward() {
      if (this.canGoForward) {
        this.visibleEndIndex = Math.min(this.allDates.length - 1, this.visibleEndIndex + 1);
        this.updateVisibleDates();
        this.workTaskPlanColumns = this.generateColumns();
        
        // Скроллим таблицу вправо
        this.$nextTick(() => {
          const wrapper = this.$refs.tableWrapper;
          if (wrapper) {
            wrapper.scrollLeft = wrapper.scrollWidth;
          }
        });
      }
    },
    shiftHalfMonthLeft() {
      if (!this.canGoBackward || !this.allDates || this.allDates.length === 0) return;

      const currentStart = this.allDates[this.visibleStartIndex];
      if (!currentStart || !currentStart.original) return;

      const [yearStr, monthStr, dayStr] = currentStart.original.split('-');
      let year = parseInt(yearStr, 10);
      let month = parseInt(monthStr, 10); // 1-12
      const day = parseInt(dayStr, 10);

      // Определяем целевую половину месяца при движении влево
      let targetYear = year;
      let targetMonth = month;
      let halfType; // 'first' или 'second'

      if (day <= 15) {
        // Сейчас показывается первая половина → идём в предыдущий месяц, в его вторую половину
        const prevMonthDate = new Date(year, month - 2, 1); // month-2, т.к. JS-месяцы 0-11
        targetYear = prevMonthDate.getFullYear();
        targetMonth = prevMonthDate.getMonth() + 1;
        halfType = 'second';
      } else {
        // Сейчас показывается вторая половина → идём в первую половину этого же месяца
        halfType = 'first';
      }

      // Старт/финиш для нужной половины
      let startDay, endDay;
      if (halfType === 'first') {
        startDay = 1;
        endDay = 15;
      } else {
        startDay = 16;
        const lastDay = new Date(targetYear, targetMonth, 0).getDate();
        endDay = lastDay;
      }

      const pad = (n) => String(n).padStart(2, '0');
      const startDateStr = `${targetYear}-${pad(targetMonth)}-${pad(startDay)}`;
      const endDateStr = `${targetYear}-${pad(targetMonth)}-${pad(endDay)}`;

      let startIndex = this.allDates.findIndex(d => d.original >= startDateStr);
      let endIndex = this.allDates.findIndex(d => d.original > endDateStr);

      if (startIndex < 0) return; // Нет дат для такой половины
      if (endIndex < 0) {
        endIndex = this.allDates.length - 1;
      } else {
        endIndex = endIndex - 1;
      }

      this.visibleStartIndex = Math.max(0, startIndex);
      this.visibleEndIndex = Math.min(this.allDates.length - 1, endIndex);
      this.updateVisibleDates();
      this.workTaskPlanColumns = this.generateColumns();

      this.$nextTick(() => {
        const wrapper = this.$refs.tableWrapper;
        if (wrapper) {
          wrapper.scrollLeft = 0;
        }
      });
    },
    shiftHalfMonthRight() {
      if (!this.canGoForward || !this.allDates || this.allDates.length === 0) return;

      const currentStart = this.allDates[this.visibleStartIndex];
      if (!currentStart || !currentStart.original) return;

      const [yearStr, monthStr, dayStr] = currentStart.original.split('-');
      let year = parseInt(yearStr, 10);
      let month = parseInt(monthStr, 10); // 1-12
      const day = parseInt(dayStr, 10);

      // Определяем целевую половину месяца при движении вправо
      let targetYear = year;
      let targetMonth = month;
      let halfType; // 'first' или 'second'

      if (day <= 15) {
        // Сейчас показывается первая половина → идём во вторую половину этого же месяца
        halfType = 'second';
      } else {
        // Сейчас показывается вторая половина → идём в первую половину следующего месяца
        const nextMonthDate = new Date(year, month, 1); // следующий месяц
        targetYear = nextMonthDate.getFullYear();
        targetMonth = nextMonthDate.getMonth() + 1;
        halfType = 'first';
      }

      // Старт/финиш для нужной половины
      let startDay, endDay;
      if (halfType === 'first') {
        startDay = 1;
        endDay = 15;
      } else {
        startDay = 16;
        const lastDay = new Date(targetYear, targetMonth, 0).getDate();
        endDay = lastDay;
      }

      const pad = (n) => String(n).padStart(2, '0');
      const startDateStr = `${targetYear}-${pad(targetMonth)}-${pad(startDay)}`;
      const endDateStr = `${targetYear}-${pad(targetMonth)}-${pad(endDay)}`;

      let startIndex = this.allDates.findIndex(d => d.original >= startDateStr);
      let endIndex = this.allDates.findIndex(d => d.original > endDateStr);

      if (startIndex < 0) return; // Нет дат для такой половины
      if (endIndex < 0) {
        endIndex = this.allDates.length - 1;
      } else {
        endIndex = endIndex - 1;
      }

      this.visibleStartIndex = Math.max(0, startIndex);
      this.visibleEndIndex = Math.min(this.allDates.length - 1, endIndex);
      this.updateVisibleDates();
      this.workTaskPlanColumns = this.generateColumns();

      this.$nextTick(() => {
        const wrapper = this.$refs.tableWrapper;
        if (wrapper) {
          wrapper.scrollLeft = wrapper.scrollWidth;
        }
      });
    },
    formatDateShort(isoDate) {
      if (!isoDate) return '';
      const parts = isoDate.split('-');
      if (parts.length === 3) {
        return `${parts[2]}.${parts[1]}`;
      }
      return isoDate;
    },
    // Загружает данные для конкретного месяца
    async loadMonthData(work_group_id, work_group_level_id, monthIndex) {
      if (!work_group_id || !this.monthGroups || this.monthGroups.length === 0) return;
      
      const monthGroup = this.monthGroups[monthIndex];
      if (!monthGroup) return;
      
      const monthKey = `${monthGroup.year}-${String(monthGroup.month).padStart(2, '0')}`;
      
      // Если данные уже в кеше, не загружаем повторно
      if (this.monthDataCache[monthKey]) {
        return;
      }
      
      try {
        // Загружаем данные для этого месяца
        const res = await getWorkTaskPlan(work_group_id, work_group_level_id);
        const groupedData = res?.data || [];
        
        // Фильтруем данные только для этого месяца
        const monthStartDate = `${monthGroup.year}-${String(monthGroup.month).padStart(2, '0')}-01`;
        const daysInMonth = new Date(monthGroup.year, monthGroup.month, 0).getDate();
        const monthEndDate = `${monthGroup.year}-${String(monthGroup.month).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
        
        // Преобразуем данные в формат для таблицы
        const flatData = [];
        groupedData.forEach((group) => {
          if (group.items && Array.isArray(group.items) && group.items.length > 0) {
            group.items.forEach((item) => {
              const normalizedDate = normalizeDateString(item.plan_date || item.work_date);
              if (!normalizedDate) return;
              
              // Фильтруем только даты этого месяца
              if (normalizedDate >= monthStartDate && normalizedDate <= monthEndDate) {
                flatData.push({
                  ...item,
                  task_name_group: group.task_name,
                  plan_date_original: item.plan_date || item.work_date,
                  plan_date: normalizedDate,
                  work_date: normalizedDate,
                });
              }
            });
          }
        });
        
        // Создаем rowsData для этого месяца
        const uniqueTaskNames = [];
        groupedData.forEach((group) => {
          if (group.task_name && !uniqueTaskNames.includes(group.task_name)) {
            uniqueTaskNames.push(group.task_name);
          }
        });
        
        const taskDictMap = {};
        const unitNameMap = {};
        const taskDateMap = {};
        
        groupedData.forEach((group) => {
          const taskName = group.task_name;
          if (!taskName) return;
          if (group.task_dict_id && !taskDictMap[taskName]) {
            taskDictMap[taskName] = group.task_dict_id;
          }
          if (group.unit_name && !unitNameMap[taskName]) {
            unitNameMap[taskName] = group.unit_name;
          }
        });
        
        flatData.forEach((item) => {
          const taskName = item.task_name || item.task_name_group;
          if (!taskName) return;
          if (item.task_dict_id && !taskDictMap[taskName]) {
            taskDictMap[taskName] = item.task_dict_id;
          }
          if (item.unit_name && !unitNameMap[taskName]) {
            unitNameMap[taskName] = item.unit_name;
          }
        });
        
        flatData.forEach((item) => {
          const taskName = item.task_name || item.task_name_group;
          if (!taskName || !item.plan_date) return;
          if (!taskDateMap[taskName]) {
            taskDateMap[taskName] = {};
          }
          if (!taskDateMap[taskName][item.plan_date]) {
            taskDateMap[taskName][item.plan_date] = [];
          }
          taskDateMap[taskName][item.plan_date].push(item);
        });
        
        const rowsData = uniqueTaskNames.map((taskName) => {
          const row = {
            task_name: taskName,
            task_dict_id: taskDictMap[taskName] || null,
            unit_name: unitNameMap[taskName] || null,
          };
          
          monthGroup.dates.forEach((dateInfo) => {
            row[dateInfo.original] = taskDateMap[taskName]?.[dateInfo.original] || [];
          });
          
          return row;
        });
        
        const columns = getWorkTaskPlanColumns(monthGroup.dates || []);
        
        // Добавляем строку этажности, если есть этажи
        if (this.levels.length > 0) {
          this.distributeFloorsByDates(monthGroup);
          this.addFloorsRowToData(rowsData, monthGroup);
        }
        
        // Сохраняем в кеш
        this.monthDataCache[monthKey] = {
          rowsData,
          columns,
        };
      } catch (e) {
        console.error('Error loading month data:', e);
        // Сохраняем пустые данные в кеш, чтобы не загружать повторно
        this.monthDataCache[monthKey] = {
          rowsData: [],
          columns: [],
        };
      }
    },
    // Загружает данные для месяца, если их нет в кеше
    async loadMonthDataIfNeeded(work_group_id, work_group_level_id, monthIndex) {
      if (!work_group_id || !this.monthGroups || this.monthGroups.length === 0) return;
      
      const monthGroup = this.monthGroups[monthIndex];
      if (!monthGroup) return;
      
      const monthKey = `${monthGroup.year}-${String(monthGroup.month).padStart(2, '0')}`;
      
      // Если данных нет в кеше, загружаем
      if (!this.monthDataCache[monthKey]) {
        await this.loadMonthData(work_group_id, work_group_level_id, monthIndex);
      }
    },
    getRowClass(row) {
      // Добавляем класс для строки "Выход людей"
      if (row.is_labor) {
        return 'labor-row';
      }
      // Добавляем класс для строки этажности
      if (row.task_name === 'Этажность') {
        return 'floors-row';
      }
      return '';
    },
    // Распределение этажей по всем датам
    distributeFloorsByAllDates() {
      this.floorDistribution = {};
      
      if (!this.levels || this.levels.length === 0) {
        console.warn('No levels to distribute');
        return;
      }
      
      if (!this.allDates || this.allDates.length === 0) {
        console.warn('No dates to distribute floors');
        return;
      }
      
      // 1 колонка = 1 level_detail, поэтому этаж берём прямо из dateInfo
      this.allDates.forEach((dateInfo) => {
        let levelNum = dateInfo.level_num;
        if (levelNum && typeof levelNum === 'string') {
          const match = levelNum.match(/^(\d+)/);
          if (match) {
            levelNum = parseInt(match[1]);
          }
        }
        this.floorDistribution[dateInfo.original] = levelNum !== null && levelNum !== undefined ? levelNum : null;
      });
    },
    // Приводит дату к формату YYYY-MM-DD для сравнения
    normalizeDateForComparison(dateStr) {
      if (!dateStr) return null;
      
      try {
        let date;
        if (dateStr.includes('T')) {
          // ISO формат с временем
          date = new Date(dateStr);
        } else if (dateStr.includes('.')) {
          // Формат DD.MM.YYYY
          const parts = dateStr.split('.');
          if (parts.length === 3) {
            date = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
          } else {
            return null;
          }
        } else if (dateStr.includes('-')) {
          // Формат YYYY-MM-DD (уже нормализован)
          const parts = dateStr.split('-');
          if (parts.length === 3) {
            return dateStr; // Уже в нужном формате
          }
          date = new Date(dateStr);
        } else {
          date = new Date(dateStr);
        }
        
        if (isNaN(date.getTime())) return null;
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        
        return `${year}-${month}-${day}`;
      } catch (e) {
        console.error('Error normalizing date:', dateStr, e);
        return null;
      }
    },
    // Добавляет строку этажности в данные таблицы
    addFloorsRowToData() {
      if (!this.allDates || this.allDates.length === 0 || this.levels.length === 0) return;
      
      const floorsRow = {
        task_name: 'Этажность',
        task_dict_id: -1, // Используем -1 вместо null для уникальности
        unit_name: '-',
      };
      
      // Для каждой даты добавляем номер этажа
      this.allDates.forEach((dateInfo) => {
        const floorNum = this.floorDistribution[dateInfo.original];
        floorsRow[dateInfo.original] = floorNum !== null && floorNum !== undefined ? [{ plan_value: floorNum }] : [];
      });
      
      // Добавляем строку в конец массива
      this.rowsData.push(floorsRow);
    },
    // Получаем стили для ячейки этажа в зависимости от номера этажа
    getFloorCellStyle(floorData) {
      if (floorData === null || floorData === undefined || floorData === '') {
        return {};
      }
      
      // Поддержка и старого формата (число/строка) и нового (объект)
      let floorNum = floorData;
      if (typeof floorData === 'object' && floorData !== null) {
        floorNum = floorData.level_num;
      }
      
      if (floorNum === null || floorNum === undefined || floorNum === '') {
        return {};
      }
      
      // Извлекаем число из строки (например "1" или "1 этаж")
      let num = 0;
      if (typeof floorNum === 'number') {
        num = floorNum;
      } else if (typeof floorNum === 'string') {
        const match = floorNum.match(/\d+/);
        num = match ? parseInt(match[0]) : 0;
      }
      
      if (num <= 0) {
        return {};
      }
      
      // Палитра цветов для этажей (Material Design цвета)
      const floorColors = [
        { bg: '#E3F2FD', border: '#90CAF9', text: '#1565C0' }, // 1 этаж - синий
        { bg: '#E8F5E9', border: '#A5D6A7', text: '#2E7D32' }, // 2 этаж - зеленый
        { bg: '#FFF3E0', border: '#FFCC80', text: '#E65100' }, // 3 этаж - оранжевый
        { bg: '#F3E5F5', border: '#CE93D8', text: '#6A1B9A' }, // 4 этаж - фиолетовый
        { bg: '#FCE4EC', border: '#F48FB1', text: '#880E4F' }, // 5 этаж - розовый
        { bg: '#E0F2F1', border: '#80CBC4', text: '#004D40' }, // 6 этаж - бирюзовый
        { bg: '#FFF9C4', border: '#FFF59D', text: '#F57F17' }, // 7 этаж - желтый
        { bg: '#EFEBE9', border: '#BCAAA4', text: '#3E2723' }, // 8 этаж - коричневый
        { bg: '#E1F5FE', border: '#81D4FA', text: '#01579B' }, // 9 этаж - голубой
        { bg: '#F1F8E9', border: '#AED581', text: '#33691E' }, // 10 этаж - светло-зеленый
      ];
      
      const colorIndex = (num - 1) % floorColors.length;
      const colors = floorColors[colorIndex];
      
      return {
        backgroundColor: colors.bg,
        borderColor: colors.border,
        color: colors.text,
      };
    },
    // Тихое обновление данных (без loader'а)
    async reloadDataSilently() {
      if (!this.filters.work_group_id) return;
      
      try {
        // Сохраняем текущую позицию перед перезагрузкой
        const savedStartIndex = this.visibleStartIndex;
        const savedEndIndex = this.visibleEndIndex;
        
        // Загружаем данные
        await this.loadWorkTaskPlanData(this.filters.work_group_id);
        
        // Восстанавливаем сохраненную позицию
        if (savedStartIndex !== undefined && savedEndIndex !== undefined && this.allDates.length > 0) {
          this.visibleStartIndex = Math.max(0, Math.min(savedStartIndex, this.allDates.length - 1));
          this.visibleEndIndex = Math.max(this.visibleStartIndex, Math.min(savedEndIndex, this.allDates.length - 1));
          this.updateVisibleDates();
          this.workTaskPlanColumns = this.generateColumns();
        }
      } catch (e) {
        console.error('Error reloading data:', e);
      }
    },
    // Тихое обновление текущего месяца для общей таблицы (без удаления кеша, без loader'а) - DEPRECATED
    async reloadCurrentMonthSilently(work_group_id, monthIndex) {
      if (!work_group_id || !this.monthGroups || this.monthGroups.length === 0) return;
      
      const monthGroup = this.monthGroups[monthIndex];
      if (!monthGroup) return;
      
      const monthKey = `${monthGroup.year}-${String(monthGroup.month).padStart(2, '0')}`;
      
      try {
        // Загружаем свежие данные из API
        const res = await getWorkTaskPlan(work_group_id, null);
        const groupedData = res?.data || [];
        
        // Фильтруем данные только для этого месяца
        const monthStartDate = `${monthGroup.year}-${String(monthGroup.month).padStart(2, '0')}-01`;
        const daysInMonth = new Date(monthGroup.year, monthGroup.month, 0).getDate();
        const monthEndDate = `${monthGroup.year}-${String(monthGroup.month).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
        
        const flatData = [];
        groupedData.forEach((group) => {
          if (group.items && Array.isArray(group.items) && group.items.length > 0) {
            group.items.forEach((item) => {
              const normalizedDate = normalizeDateString(item.plan_date || item.work_date);
              if (!normalizedDate) return;
              if (normalizedDate >= monthStartDate && normalizedDate <= monthEndDate) {
                flatData.push({
                  ...item,
                  task_name_group: group.task_name,
                  plan_date_original: item.plan_date || item.work_date,
                  plan_date: normalizedDate,
                  work_date: normalizedDate,
                });
              }
            });
          }
        });
        
        const uniqueTaskNames = [];
        groupedData.forEach((group) => {
          if (group.task_name && !uniqueTaskNames.includes(group.task_name)) {
            uniqueTaskNames.push(group.task_name);
          }
        });
        
        const taskDictMap = {};
        const unitNameMap = {};
        const taskDateMap = {};
        
        groupedData.forEach((group) => {
          const taskName = group.task_name;
          if (!taskName) return;
          if (group.task_dict_id && !taskDictMap[taskName]) {
            taskDictMap[taskName] = group.task_dict_id;
          }
          if (group.unit_name && !unitNameMap[taskName]) {
            unitNameMap[taskName] = group.unit_name;
          }
        });
        
        flatData.forEach((item) => {
          const taskName = item.task_name || item.task_name_group;
          if (!taskName) return;
          if (item.task_dict_id && !taskDictMap[taskName]) {
            taskDictMap[taskName] = item.task_dict_id;
          }
          if (item.unit_name && !unitNameMap[taskName]) {
            unitNameMap[taskName] = item.unit_name;
          }
        });
        
        flatData.forEach((item) => {
          const taskName = item.task_name || item.task_name_group;
          if (!taskName || !item.plan_date) return;
          if (!taskDateMap[taskName]) {
            taskDateMap[taskName] = {};
          }
          if (!taskDateMap[taskName][item.plan_date]) {
            taskDateMap[taskName][item.plan_date] = [];
          }
          taskDateMap[taskName][item.plan_date].push(item);
        });
        
        const rowsData = uniqueTaskNames.map(taskName => {
          const row = {
            task_name: taskName,
            task_dict_id: taskDictMap[taskName] || null,
            unit_name: unitNameMap[taskName] || '',
          };
          monthGroup.dates.forEach((dateInfo) => {
            const dateKey = dateInfo.original;
            row[dateKey] = taskDateMap[taskName]?.[dateKey] || [];
          });
          return row;
        });
        
        // Добавляем строку этажности, если есть этажи
        if (this.levels.length > 0) {
          this.distributeFloorsByDates(monthGroup);
          this.addFloorsRowToData(rowsData, monthGroup);
        }
        
        // Просто присваиваем новые данные - Vue реактивно обновит только изменённые элементы
        this.rowsData = rowsData;
        
        // Обновляем кеш
        this.monthDataCache[monthKey] = {
          rowsData,
          columns: this.columns,
        };
      } catch (error) {
        console.error('Error reloading month data:', error);
      }
    },
  },
  setup() {
    const { showModal, closeModal } = useAppStore();
    const { showSucceedSnackbar } = useCustomSnackbar();
    return { showModal, closeModal, showSucceedSnackbar };
  }
}
</script>

<style scoped lang="scss">
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px;
  margin-bottom: 25px;

  &__item {
    min-width: 150px;
    
    // Убираем курсор блокировки для disabled селектов
    :deep(.q-field--disabled) {
      cursor: default !important;
      
      .q-field__control {
        cursor: default !important;
      }
      
      .q-field__native {
        cursor: default !important;
      }
      
      .q-field__append {
        cursor: default !important;
      }
      
      .q-icon {
        cursor: default !important;
      }
    }
    
    // Убираем курсор блокировки для всех элементов внутри селектов
    :deep(.q-field) {
      .q-field__append {
        .q-icon {
          cursor: pointer !important;
        }
      }
    }
    
    :deep(.q-field--disabled) {
      .q-field__append {
        .q-icon {
          cursor: default !important;
        }
      }
    }
  }
}

.table-container {
  width: 100%;
  position: relative;
}

.table-header-with-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 12px;
  background: #f5f5f5;
  border-bottom: 1px solid #ddd;
  
  .table-title-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    min-width: 200px;
  }
  
  .table-title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    text-align: center;
  }
  
  .contractor-info {
    font-size: 15px;
    color: #1976d2;
    font-weight: 600;
    text-align: center;
    padding: 6px 12px;
    background: #e3f2fd;
    border-radius: 4px;
    border: 1px solid #90caf9;
    margin-top: 4px;
  }
  
  .nav-btn {
    color: #1976d2;
    
    &:hover:not(:disabled) {
      background: rgba(25, 118, 210, 0.1);
    }
    
    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }
}

.nav-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.nav-range-btn {
  font-size: 11px;
  text-transform: none;
  padding: 0 6px;
}

.copy-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.toolbar-guide-card {
  max-width: 960px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.toolbar-guide-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
}

.toolbar-guide-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toolbar-guide-title-icon {
  color: #1976d2;
}

.toolbar-guide-header .title-text {
  font-size: 18px;
  font-weight: 600;
}

.toolbar-guide-header .subtitle-text {
  font-size: 13px;
  color: #666;
}

.toolbar-guide-body {
  padding: 16px 20px 4px;
  overflow-y: auto;
}

.guide-section {
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.guide-section:last-of-type {
  border-bottom: none;
}

.guide-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  margin-bottom: 6px;
}

.guide-section-title span {
  font-size: 14px;
}

.guide-section-content {
  font-size: 13px;
  color: #444;
}

.guide-section-content ul,
.guide-section-content ol {
  padding-left: 18px;
  margin: 4px 0 0;
}

.guide-section-content li {
  margin-bottom: 2px;
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
    // Резервируем место под скроллбар, чтобы он не налезал на контент
    scrollbar-gutter: stable;
    padding-bottom: 18px; // Отступ снизу для скроллбара (12px высота + 6px отступ)
    
    // Стилизация скроллбара - красивый градиентный дизайн
    scrollbar-width: thin;
    scrollbar-color: rgba(25, 118, 210, 0.8) rgba(245, 245, 245, 0.5);
    
    &::-webkit-scrollbar {
      height: 14px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f8f9fa;
      border-radius: 8px;
      margin: 4px 12px;
      box-shadow: inset 0 0 2px rgba(0, 0, 0, 0.05);
    }
    
    &::-webkit-scrollbar-thumb {
      background: linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%);
      border-radius: 8px;
      border: 2px solid #f8f9fa;
      transition: all 0.3s ease;
      box-shadow: 0 2px 4px rgba(25, 118, 210, 0.2);
      
      &:hover {
        background: linear-gradient(90deg, #1565c0 0%, #1e88e5 50%, #1565c0 100%);
        box-shadow: 0 2px 6px rgba(25, 118, 210, 0.3);
      }
      
      &:active {
        background: linear-gradient(90deg, #0d47a1 0%, #1565c0 50%, #0d47a1 100%);
      }
    }
    
    &::-webkit-scrollbar-button {
      display: none;
      width: 0;
      height: 0;
    }
    
    &::-webkit-scrollbar-corner {
      background: transparent;
    }
}

// Стили для ячейки со значением
.date-cell--has-value {
  background-color: #e3f2fd !important;
  font-weight: 600;
  font-size: 14px;
  color: #1976d2;
  
  &:hover {
    background-color: #bbdefb !important;
  }
}

.selected-cell {
  box-shadow: 0 0 0 2px #1976d2 inset;
  background-color: rgba(25, 118, 210, 0.08) !important;
}

.drag-fill-preview {
  box-shadow: 0 0 0 2px #ff9800 inset;
  background-color: rgba(255, 152, 0, 0.12) !important;
}

.cell-inner {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cell-content {
  pointer-events: none;
}

.fill-handle {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 8px;
  height: 8px;
  background-color: #ff9800;
  border-radius: 2px;
  box-shadow: 0 0 0 1px #ef6c00;
}

.plan-value-text {
  display: inline-block;
  font-size: 14px;
  font-weight: 600;
}

.task-name-header {
  background-color: #f5f5f5;
  width: 250px;
  min-width: 250px;
  max-width: 250px;
  position: sticky;
  left: 0;
  z-index: 10;
  font-weight: 600;
  padding: 8px;
}

.task-name-cell {
  width: 250px;
  min-width: 250px;
  max-width: 250px;
  position: sticky;
  left: 0;
  z-index: 5;
  background-color: white;
}

.unit-name-header {
  background-color: #f5f5f5;
  width: 70px;
  min-width: 70px;
  max-width: 70px;
  position: sticky;
  left: 250px;
  z-index: 10;
  text-align: center;
  font-weight: 600;
  padding: 8px;
}

.date-header {
  min-width: 80px;
  max-width: 80px;
  width: 80px;
  padding: 8px 4px;
  text-align: center;
  background-color: #f5f5f5;
  font-weight: 600;
  border: 1px solid #d0d0d0;
  
  &.date-header--today {
    background: #dae8fc !important;
    font-weight: 700;
  }

  &.date-header--weekend {
    background: #ffebee !important;
  }

  &.date-header--today.date-header--weekend {
    background: #ffc1c1 !important;
  }
}

.date-header-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.date-weekday {
  font-size: 11px;
  font-weight: 600;
  color: #666;
}

.date-header--weekend .date-weekday {
  color: #c62828;
  font-weight: 700;
}

.date-display {
  font-size: 12px;
  font-weight: 600;
  color: #000;
}

.date-display.date-changed {
  color: #e65100;
}

.date-original {
  font-size: 9px;
  color: #999;
  margin-top: 1px;
}

.unit-name-cell {
  width: 70px;
  min-width: 70px;
  max-width: 70px;
  position: sticky;
  left: 250px;
  z-index: 5;
  background-color: white;
  text-align: center;
}

.month-header {
  text-align: center;
  font-weight: 600;
  background-color: #f5f5f5;
  border-left: 2px solid #1976d2;
  border-right: 2px solid #1976d2;
}

.month-header-content {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  padding-left: 12px;
}

.month-title {
  min-width: 150px;
  font-weight: 600;
}

.month-nav-btn {
  color: #1976d2;
  
  &:hover:not(:disabled) {
    background-color: rgba(25, 118, 210, 0.1);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}

.month-divider {
  width: 2px;
  min-width: 2px;
  max-width: 2px;
  background-color: #1976d2;
  padding: 0 !important;
  border: none !important;
}

.month-divider-cell {
  width: 2px;
  min-width: 2px;
  max-width: 2px;
  background-color: #e0e0e0;
  padding: 0 !important;
  border-left: 2px solid #1976d2;
  border-right: 2px solid #1976d2;
}

.day-header {
  font-weight: 500;
  background-color: #fafafa;
  min-width: 50px;
  width: auto;
  padding: 8px 12px;
}

.day-header--month-first {
  border-left: 2px solid #1976d2 !important;
}

.day-header--month-last {
  border-right: 2px solid #1976d2 !important;
}

.date-cell {
  border-left: 1px solid #e0e0e0;
  border-right: 1px solid #e0e0e0;
  min-width: 50px;
  width: auto;
  white-space: nowrap;
  padding: 8px 12px;
}

.clickable-cell {
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f0f0f0;
  }
}

.highlighted-row {
  background-color: #e3f2fd !important;
  
  &--first {
    border-top: 3px solid #1976d2 !important;
  }
  
  &--last {
    border-bottom: 3px solid #1976d2 !important;
    margin-bottom: 2px; // Дополнительный отступ для последней строки, чтобы скроллбар не налезал
  }
  
  .task-name-cell,
  .unit-name-cell {
    background-color: #e3f2fd !important;
  }
  
  td {
    background-color: #e3f2fd !important;
  }
  
  &:hover {
    background-color: #bbdefb !important;
    
    .task-name-cell,
    .unit-name-cell {
      background-color: #bbdefb !important;
    }
    
    td {
      background-color: #bbdefb !important;
    }
  }
}

.level-row {
  background-color: #fff9e6 !important;
  
  .task-name-cell,
  .unit-name-cell {
    background-color: #fff9e6 !important;
    font-weight: 600;
  }
  
  td {
    background-color: #fff9e6 !important;
  }
}

.level-row--hovered {
  .task-name-cell,
  .unit-name-cell {
    background-color: #ffe193 !important;
  }
}

.level-cell {
  background-color: #fff3cd !important;
  font-weight: 600;
  color: #856404;
  border: none;
  
  &:hover {
    background-color: #ffe69c !important;
  }
}

.level-cell--active {
  background-color: #ffe193 !important;
  color: #5c3b00;
}

.level-cell--active-first {
  box-shadow: inset 0 1px 0 #ffc107, inset 0 -1px 0 #ffc107, inset 2px 0 0 #ffc107;
}

.level-cell--active-last {
  box-shadow: inset 0 1px 0 #ffc107, inset 0 -1px 0 #ffc107, inset -2px 0 0 #ffc107;
}

.date-cell--level-hovered {
  background-color: #fff4d7 !important;
}

.date-cell--level-hovered-first {
  box-shadow: inset 0 1px 0 #ffc107, inset 0 -1px 0 #ffc107, inset 2px 0 0 #ffc107;
}

.date-cell--level-hovered-last {
  box-shadow: inset 0 1px 0 #ffc107, inset 0 -1px 0 #ffc107, inset -2px 0 0 #ffc107;
}

.date-cell--weekend {
  background-color: #fff5f5 !important;
}

.date-cell--today {
  position: relative;
  background-color: #ffebee !important;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background-color: #e53935;
    z-index: 1;
  }
}

.date-cell--today.date-cell--weekend {
  background-color: #ffe0e0 !important;
}

.date-cell--month-first {
  border-left: 2px solid #1976d2 !important;
}

.date-cell--month-last {
  border-right: 2px solid #1976d2 !important;
}

.day-header--level-hovered {
  background-color: #ffe193 !important;
  color: #5c3b00;
}

.day-header--level-hovered-first {
  box-shadow: inset 2px 0 0 #ffc107;
}

.day-header--level-hovered-last {
  box-shadow: inset -2px 0 0 #ffc107;
}

.level-header-row {
  background-color: #e3f2fd !important;
  font-weight: 600;
  cursor: pointer;
  
  .task-name-cell,
  .unit-name-cell {
    background-color: #e3f2fd !important;
  }
  
  td {
    background-color: #e3f2fd !important;
  }
  
  &:hover {
    background-color: #bbdefb !important;
    
    .task-name-cell,
    .unit-name-cell {
      background-color: #bbdefb !important;
    }
    
    td {
      background-color: #bbdefb !important;
    }
  }
}

.level-header-content {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.level-toggle-icon {
  font-size: 20px;
  transition: transform 0.2s;
  cursor: pointer;
  
  &:hover {
    color: #1976d2;
  }
}

.level-header-text {
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    color: #1976d2;
  }
}

.no-data {
  padding: 40px;
  text-align: center;
  color: #999;
  font-size: 16px;
}

.error-message {
  padding: 40px;
  text-align: center;
  color: #c10015;
  font-size: 16px;
  font-weight: 500;
  background: #ffebee;
  border: 1px solid #ef5350;
  border-radius: 4px;
  margin: 20px;
}

// Стили для строки "Выход людей"
.labor-row {
  background: #fff3e0 !important;
}

.labor-task-cell {
  background: #fff3e0 !important;
  border: 1px solid #ffb74d !important;
}

.labor-unit-cell {
  background: #fff3e0 !important;
  border: 1px solid #ffb74d !important;
}

.labor-task-name {
  font-weight: 700;
  color: #e65100;
  font-size: 16px;
}

.labor-cell {
  padding: 8px;
  text-align: center;
  font-weight: 600;
  font-size: 14px;
  border: 1px solid #ffe0b2;
  background: #fff3e0 !important;
  
  &:hover {
    background: #ffe0b2 !important;
  }
  
  &.date-cell--has-value {
    background-color: #ffcc80 !important;
    color: #e65100;
    
    &:hover {
      background-color: #ffb74d !important;
    }
  }
  
  &.date-cell--today {
    border-width: 2px;
    border-left: 3px solid #ff6f00 !important;
  }
}

.labor-cell-content {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  font-weight: 600;
  font-size: 14px;
}

// Стили для строки этажей
.floors-row {
  background: #e3f2fd !important;
}

.floors-task-cell {
  background: #e3f2fd !important;
  border: 1px solid #90caf9 !important;
}

.floors-unit-cell {
  background: #e3f2fd !important;
  border: 1px solid #90caf9 !important;
}

.floors-task-name {
  font-weight: 700;
  color: #1565c0;
  font-size: 16px;
}

// Стили для ячейки этажа
.floor-cell {
  padding: 8px;
  text-align: center;
  font-weight: 700;
  font-size: 16px;
  cursor: default;
  border: 1px solid;
  
  &.date-cell--today {
    border-width: 2px;
  }
}

.floor-cell-content {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  font-weight: 700;
  font-size: 16px;
}
</style>

